/** B2B invoice queries + mutations (BP-09) via @gym/api-client. RLS scopes rows
 *  to the tenant (owner/manager read-only policy on b2b_invoices). */
import {
  type B2BInvoiceListFilter,
  type B2BInvoiceView,
  type CreateB2BInvoiceResult,
  createB2BInvoice,
  getB2BInvoice,
  listB2BInvoices,
  newIdempotencyKey,
  sendB2BInvoice,
  voidB2BInvoice,
} from '@gym/api-client';
import type { schemas } from '@gym/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../../lib/supabase.js';

export type { B2BInvoiceView, CreateB2BInvoiceResult };

export function useB2BInvoices(filter: B2BInvoiceListFilter = {}) {
  return useQuery({
    queryKey: ['b2b-invoices', filter],
    queryFn: async (): Promise<B2BInvoiceView[]> => {
      const res = await listB2BInvoices(supabase, filter);
      return res.invoices;
    },
  });
}

export function useB2BInvoice(id: string | null) {
  return useQuery({
    queryKey: ['b2b-invoice', id],
    enabled: !!id,
    queryFn: async (): Promise<B2BInvoiceView> => getB2BInvoice(supabase, id as string),
  });
}

export function useCreateB2BInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      req: Omit<schemas.B2BInvoiceRequest, 'idempotency_key'> & { idempotency_key?: string },
    ): Promise<CreateB2BInvoiceResult> =>
      createB2BInvoice(supabase, {
        ...req,
        idempotency_key: req.idempotency_key ?? newIdempotencyKey(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['b2b-invoices'] });
    },
  });
}

export function useSendB2BInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => sendB2BInvoice(supabase, id),
    onSuccess: (_d, id) => {
      void qc.invalidateQueries({ queryKey: ['b2b-invoices'] });
      void qc.invalidateQueries({ queryKey: ['b2b-invoice', id] });
    },
  });
}

export function useVoidB2BInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => voidB2BInvoice(supabase, id),
    onSuccess: (_d, id) => {
      void qc.invalidateQueries({ queryKey: ['b2b-invoices'] });
      void qc.invalidateQueries({ queryKey: ['b2b-invoice', id] });
    },
  });
}
